import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import services from 'services';
import {
    WithdrawalConfirmResponse,
    WithdrawalLookupRequest,
    WithdrawalOrder,
    WithdrawalSelectionItem,
} from 'services/withdrawal';
import { RootState } from 'store';

export type WithdrawalLookupStatus = 'idle' | 'pending' | 'loaded' | 'notFound' | 'rateLimited' | 'error';
export type WithdrawalConfirmStatus =
    | 'idle'
    | 'pending'
    | 'success'
    | 'notFound'
    | 'expired'
    | 'invalid'
    | 'rateLimited'
    | 'error';

type WithdrawalState = {
    lookupStatus: WithdrawalLookupStatus;
    order: WithdrawalOrder | null;
    isWithinWindow: boolean;
    windowEndsAt: string | null;
    email: string | null;
    selection: Record<string, number>;
    confirmStatus: WithdrawalConfirmStatus;
    acknowledgement: WithdrawalConfirmResponse | null;
};

const initialState: WithdrawalState = {
    lookupStatus: 'idle',
    order: null,
    isWithinWindow: false,
    windowEndsAt: null,
    email: null,
    selection: {},
    confirmStatus: 'idle',
    acknowledgement: null,
};

const lookupStatusFrom = (status: number): WithdrawalLookupStatus =>
    status === 404 ? 'notFound' : status === 429 ? 'rateLimited' : 'error';

const confirmStatusFrom = (status: number): WithdrawalConfirmStatus => {
    switch (status) {
        case 404:
            return 'notFound';
        case 409:
            return 'expired';
        case 400:
            return 'invalid';
        case 429:
            return 'rateLimited';
        default:
            return 'error';
    }
};

export const lookupWithdrawalOrder = createAsyncThunk<
    { order: WithdrawalOrder; isWithinWindow: boolean; windowEndsAt: string },
    WithdrawalLookupRequest,
    { rejectValue: WithdrawalLookupStatus }
>('withdrawal/lookup', async (request, { rejectWithValue }) => {
    const result = await services.withdrawal.lookup(request);
    if (!result.ok) return rejectWithValue(lookupStatusFrom(result.status));
    return result.data;
});

export const confirmWithdrawal = createAsyncThunk<
    WithdrawalConfirmResponse,
    void,
    { state: RootState; rejectValue: WithdrawalConfirmStatus }
>('withdrawal/confirm', async (_, { getState, rejectWithValue }) => {
    const { order, email, selection } = getState().withdrawal;
    if (!order || !email) return rejectWithValue('notFound');

    const selections: WithdrawalSelectionItem[] = order.lines
        .filter((line) => (selection[line.lineId] ?? 0) > 0)
        .map((line) => ({ lineId: line.lineId, sku: line.sku, quantity: selection[line.lineId] }));

    const result = await services.withdrawal.confirm({ orderNumber: order.orderNumber, email, selections });
    if (!result.ok) return rejectWithValue(confirmStatusFrom(result.status));
    return result.data;
});

const withdrawalSlice = createSlice({
    name: 'withdrawal',
    initialState,
    reducers: {
        withdrawalQuantitySet(state, action: PayloadAction<{ lineId: string; quantity: number }>) {
            const { lineId, quantity } = action.payload;
            const ordered = state.order?.lines.find((line) => line.lineId === lineId)?.quantity ?? 0;
            const bounded = Math.max(0, Math.min(ordered, Math.floor(quantity) || 0));
            if (bounded === 0) delete state.selection[lineId];
            else state.selection[lineId] = bounded;
            if (state.confirmStatus !== 'pending') state.confirmStatus = 'idle';
        },
        withdrawalReset() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(lookupWithdrawalOrder.pending, () => ({ ...initialState, lookupStatus: 'pending' }));
        builder.addCase(lookupWithdrawalOrder.fulfilled, (state, action) => {
            state.lookupStatus = 'loaded';
            state.order = action.payload.order;
            state.isWithinWindow = action.payload.isWithinWindow;
            state.windowEndsAt = action.payload.windowEndsAt;
            state.email = action.meta.arg.email;
        });
        builder.addCase(lookupWithdrawalOrder.rejected, (state, action) => {
            state.lookupStatus = action.payload ?? 'error';
        });
        builder.addCase(confirmWithdrawal.pending, (state) => {
            state.confirmStatus = 'pending';
        });
        builder.addCase(confirmWithdrawal.fulfilled, (state, action) => {
            state.confirmStatus = 'success';
            state.acknowledgement = action.payload;
        });
        builder.addCase(confirmWithdrawal.rejected, (state, action) => {
            state.confirmStatus = action.payload ?? 'error';
        });
    },
});

export const { withdrawalQuantitySet, withdrawalReset } = withdrawalSlice.actions;

export const selectWithdrawal = (state: RootState) => state.withdrawal;
export const selectWithdrawalSelectedCount = (state: RootState) =>
    Object.values(state.withdrawal.selection).reduce((sum, quantity) => sum + quantity, 0);

export default withdrawalSlice.reducer;
